import { useRef } from 'react';

import { CheckIcon } from '@heroicons/react/24/outline';
import { useRouteLoaderData, useSearchParams } from '@remix-run/react';
import cx from 'classnames';

import Typography from '~/components/Typography';
import useEmail from '~/hooks/useEmail';
import type { Price, Tier } from '~/interfaces/subscriptions';
import { PriceInterval } from '~/interfaces/subscriptions';
import type { UpgradePage } from '~/interfaces/upgrade_page';
import type { RootLoaderData } from '~/root';
import normalizeFontType from '~/utils/normalizeFontType';

import TierPriceBlock from './components/TierPriceBlock';

import PayForm from '../PayForm';
import Badge from './Badge';

interface Props {
  tier: Tier;
  upgradePage: UpgradePage;
  selectedPriceId: string;
  isExternalCheckoutEnabled: boolean;
  onPriceIdChange: (priceId: string) => void;
  onContinue: () => void;
}

const DonationCard = ({
  tier,
  upgradePage,
  selectedPriceId,
  isExternalCheckoutEnabled = false,
  onPriceIdChange,
  onContinue,
}: Props) => {
  const [searchParams] = useSearchParams();
  const isActive = tier.prices.some((price) => price.id === selectedPriceId);
  const price = tier.prices.find((price) => price.interval === PriceInterval.DONATION);
  const selectedPrice = price as Price;
  const [email] = useEmail();
  const { subscription } = useRouteLoaderData('root') as RootLoaderData;
  const emailInSubscription = subscription?.email;
  const premiumOfferId = searchParams.get('offer_id') || selectedPrice.premium_offer?.id || '';

  const recommendedText = upgradePage.main_section.most_popular_plan_label_text;

  const backgroundColor = upgradePage.main_section.card_bg_color;
  const accentColor = upgradePage.main_section.card_accent_color;
  const borderColor = upgradePage.main_section.card_border_color;
  const fontFamily = normalizeFontType(upgradePage.main_section.card_font_family);
  const cardPriceFontSize = upgradePage.main_section.card_price_font_size;
  const txtColor = upgradePage.main_section.card_text_color;
  const highlightBgColor = upgradePage.main_section.most_popular_plan_bg_color;
  const highlightTextColor = upgradePage.main_section.most_popular_plan_label_color;
  const ctaBgColor = upgradePage.main_section.checkout_cta_bg_color;
  const ctaTextColor = upgradePage.main_section.checkout_cta_text_color;

  const priceTabsRef = useRef(null);
  const isRecommendedPrice = selectedPrice.id === upgradePage.main_section.most_popular_price_id;

  const handleChangeInTier = (_tierId: string) => {
    onPriceIdChange(selectedPrice.id);
  };

  return (
    <div
      key={tier.id}
      className={cx(
        'relative flex h-full w-72 rounded-lg border bg-white p-6 shadow-sm focus:outline-none lg:w-80 xl:w-96',
        {
          'cursor-pointer': !isExternalCheckoutEnabled,
        },
      )}
      style={{
        backgroundColor,
        border: isActive && !isExternalCheckoutEnabled ? `1px solid ${accentColor}` : `1px solid ${borderColor}`,
      }}
      onClick={(e) => {
        // @ts-expect-error
        if (priceTabsRef.current !== e.target || !priceTabsRef.current?.contains(e.target)) {
          handleChangeInTier(tier.id);
        }
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleChangeInTier(tier.id);
        }
      }}
    >
      <div className="flex flex-col w-full space-y-4">
        <Typography size="xl" className="truncate" color={txtColor}>
          {tier.name}
        </Typography>

        {selectedPrice.premium_offer && (
          <Badge
            highlightBgColor={highlightBgColor}
            text={selectedPrice.premium_offer.name}
            fontFamily={fontFamily}
            highlightTextColor={highlightTextColor}
          />
        )}

        {isRecommendedPrice && !selectedPrice.premium_offer && (
          <Badge
            highlightBgColor={highlightBgColor}
            text={recommendedText}
            fontFamily={fontFamily}
            highlightTextColor={highlightTextColor}
          />
        )}

        <TierPriceBlock
          fontFamily={fontFamily}
          priceFontSize={cardPriceFontSize}
          accentColor={accentColor}
          price={selectedPrice}
          txtColor={txtColor}
          isFreePlan={false}
          isDonationPlan={true}
          isExternalCheckoutEnabled={isExternalCheckoutEnabled}
        />

        {/* Tier Description */}
        <Typography as="span" size="xs" weight="light" fontFamily={fontFamily} color={txtColor} className="break-words">
          {tier.description}
        </Typography>

        {/* Tier Features */}
        {selectedPrice?.features?.length ? (
          <ul
            style={{
              borderTopColor: accentColor,
            }}
            className={cx({
              'space-y-3 pt-0 text-sm leading-6': true,
              'border-t pt-4': tier.description || selectedPrice.premium_offer,
            })}
          >
            {selectedPrice.features.map((feature: string) => (
              <li key={feature} className="flex gap-x-2">
                <div>
                  <div style={{ color: accentColor }}>
                    <CheckIcon className={cx({ 'mt-0.5 h-4 w-4 flex-none': true })} aria-hidden="true" />
                  </div>
                </div>
                <Typography
                  size="xs"
                  weight="light"
                  fontFamily={fontFamily}
                  color={txtColor}
                  className="w-full break-words"
                >
                  {feature}
                </Typography>
              </li>
            ))}
          </ul>
        ) : null}

        {/* Tier Actions */}
        {selectedPrice && isExternalCheckoutEnabled && (
          <PayForm
            text={selectedPrice.cta}
            priceId={selectedPrice.id}
            cardRequired={true}
            buttonStyle={{
              backgroundColor: ctaBgColor,
              color: ctaTextColor,
            }}
            fontFamily={fontFamily}
            premiumOfferId={premiumOfferId}
            email={emailInSubscription || email}
            onContinue={onContinue}
          />
        )}
      </div>
    </div>
  );
};

export default DonationCard;
